import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Clock, Package, Star, 
  Bike, Store, Coffee, ShoppingBag, 
  MapPin, Zap 
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const Home = () => {
  const stats = [
    { number: "55+", label: "Professional Bikers", icon: <Bike size={14} className="text-[#0099FF]" /> },
    { number: "20k+", label: "Budapest Deliveries", icon: <Package size={14} className="text-[#0099FF]" /> },
    { number: "99%", label: "Partner Trust", icon: <Star size={14} className="text-[#0099FF]" /> },
    { number: "25m", label: "Fastest Pickup", icon: <Clock size={14} className="text-[#0099FF]" /> }
  ];

  return (
    <main className="min-h-screen bg-white text-[#032B53] font-sans selection:bg-[#0099FF] selection:text-white overflow-x-hidden">
      
      {/* 1. SEO METADATA (HIDDEN) */}
      <div className="hidden">
        <h1>Professional Biker Delivery Service Budapest Hungary</h1>
        <p>Courier partners for restaurants, cafes, hotels, and e-commerce shops in Budapest.</p>
      </div>

      {/* 2. HERO SECTION */}
      <section className="relative min-h-screen flex items-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <motion.img 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
            src="https://plus.unsplash.com/premium_photo-1680721310331-7c5bea03392d?q=80&w=1400&auto=format&fit=crop"
            className="w-full h-full object-cover brightness-[0.3]"
            alt="Budapest Delivery Fleet"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#032B53]/60 to-[#032B53]"></div>
        </div>

        <div className="max-w-7xl mx-auto w-full relative z-10">
          <motion.div {...fadeInUp}>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-[#0099FF]"></span>
              <span className="text-[#0099FF] text-[10px] uppercase tracking-[0.4em] font-bold">Elite Biker Fleet • Budapest</span>
            </div>
            
            <h2 className="text-5xl md:text-8xl font-black text-white leading-[0.9] tracking-tighter mb-8">
              Reliable Fleet <br /> 
              <span className="text-white/30 italic">For Business.</span>
            </h2>

            <p className="max-w-xl text-lg md:text-xl text-white/70 mb-10 font-light leading-relaxed">
              Scale your delivery capacity in **Budapest**. We provide professional biker logistics for **Restaurants, E-commerce, and Hotels**.
            </p>

            <motion.button 
              whileHover={{ x: 10 }}
              className="group flex items-center gap-4 bg-[#0099FF] text-white px-8 py-4 rounded-full font-bold text-sm transition-all shadow-xl shadow-blue-500/20"
            >
              BECOME A PARTNER
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 3. MINIMAL STATS BAR */}
      <section className="bg-white py-12 border-b border-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, i) => (
              <motion.div key={i} variants={fadeInUp} className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  {stat.icon}
                  <span className="text-3xl font-black tracking-tighter">{stat.number}</span>
                </div>
                <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 4. SERVICES - SEO KEYWORD RICH */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { title: "Restaurants & Cafes", icon: <Coffee />, desc: "Rapid food delivery across Budapest districts with thermal handling." },
              { title: "E-commerce Shops", icon: <ShoppingBag />, desc: "Final-mile parcel delivery for online stores and retail brands." },
              { title: "Hotel Logistics", icon: <Store />, desc: "Professional document and luggage transport for hospitality partners." }
            ].map((item, i) => (
              <motion.div 
                key={i}
                whileHover={{ y: -5 }}
                className="p-8 bg-white rounded-3xl border border-gray-100 shadow-sm"
              >
                <div className="w-10 h-10 bg-[#0099FF]/10 rounded-xl flex items-center justify-center text-[#0099FF] mb-6">
                  {React.cloneElement(item.icon, { size: 20 })}
                </div>
                <h3 className="text-xl font-bold mb-3 tracking-tight">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. BUDAPEST DISTRICT SPECIALISTS */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="inline-flex items-center gap-2 px-4 py-2 bg-[#032B53]/5 rounded-full mb-6">
            <MapPin size={12} className="text-[#0099FF]" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Available in all Budapest Districts</span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8 leading-tight">
            Fastest Delivery in <br />
            <span className="text-[#0099FF]">Hungary.</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
             {["Wolt Partner", "Express Delivery", "Fleet Outsourcing", "B2B Logistics"].map((tag, i) => (
               <span key={i} className="text-[11px] font-bold text-gray-400 border border-gray-200 px-4 py-1 rounded-full">{tag}</span>
             ))}
          </div>
        </div>
      </section>

      {/* 6. CALL TO ACTION */}
      <section className="pb-24 px-6">
        <motion.div 
          whileHover={{ scale: 1.01 }}
          className="max-w-7xl mx-auto bg-[#032B53] rounded-[2.5rem] p-12 md:p-24 text-white text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-12 opacity-10">
            <Zap size={200} />
          </div>
          <h2 className="text-4xl md:text-7xl font-black tracking-tighter mb-8 relative z-10">
            Ready to deploy?
          </h2>
          <p className="text-white/60 mb-12 text-lg max-w-xl mx-auto font-light">
            Connect your Budapest business to our professional biker network today.
          </p>
          <button className="bg-white text-[#032B53] px-12 py-5 rounded-full font-black text-sm hover:bg-[#0099FF] hover:text-white transition-all">
            GET A QUOTE
          </button>
        </motion.div>
      </section>

      {/* FOOTER SEO */}
      {/* <footer className="py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Bike size={14} className="text-[#0099FF]" />
            <span className="text-sm font-black tracking-tighter uppercase">Budapest Fleet</span>
          </div>
          <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase text-center">
            © 2026 Hungary Biker Logistics • Reliable Partners for Restaurants & E-commerce
          </p>
        </div>
      </footer> */}
    </main>
  );
};

export default Home;